/***********************************************************************
 An instance of this class handles multiple sco's and act to the LMS
 as a single SCO with the given Scorm 1.3 API.
 The stored data will be saved compresses and encrypted in the
 suspend data field on LMS.
 ***********************************************************************/
import {SingleSco2004Data} from "scorm/scorm_1_3/SingleSco2004Data";
import {Sco2004Data} from "scorm/scorm_1_3/Sco2004Data";
import {StatusCalculator} from "scorm/scorm_1_3/Scorm2004CourseStatusCalculator";

import {ScormApiBase} from "scorm/common/ScormApiBase";
import {ExternalScormApiWrapper} from "scorm/common/ExternalScormApiWrapper";
import {ScoRegistry} from "scorm/common/ScoRegistry";

import {LoggingService} from "logging/LoggingService";
import {Assert} from "common/Assert";

export function Scorm2004Course(lmsScormApi, defaultMasteryScore = 80) {

    Assert.checkArgument(lmsScormApi);

    const lmsApi = new ExternalScormApiWrapper(lmsScormApi, {
            initialize: "Initialize",
            terminate: "Terminate",
            getValue: "GetValue",
            setValue: "SetValue",
            commit: "Commit",
            getLastError: "GetLastError",
            getErrorString: "GetErrorString",
            getDiagnostic: "GetDiagnostic"
        }),
        base = new ScormApiBase(defaultMasteryScore),
        courseScoData = new SingleSco2004Data(),
        scoRegistry = new ScoRegistry(Sco2004Data);

    function getMasteryScore() {
        return parseFloat(courseScoData.get("cmi.completion_threshold"));
    }

    function refreshCourseStatus() {
        const status = StatusCalculator.calculateStatus(scoRegistry.getStatus(), getMasteryScore());
        courseScoData.set("cmi.score.raw", status.score.toString());
        courseScoData.set("cmi.completion_status", status.completionStatus);
        courseScoData.set("cmi.success_status", status.successStatus);
        courseScoData.set("cmi.exit", status.exitStatus);
        courseScoData.set("cmi.suspend_data", scoRegistry.collectSuspendData());
        courseScoData.set("cmi.session_time", base.getLapsedTime());
        courseScoData.set("cmi.progress_measure", status.progressMeasure.toString());
    }

    function getCompletionThreshold() {
        const completionThreshold = lmsApi.getValue("cmi.completion_threshold");
        if ((completionThreshold !== "") && !isNaN(Number(completionThreshold))) {
            return completionThreshold.toString();
        }

        // if no completionThreshold given, fallback to the masteryscore.
        return (base.getMasteryScore() / 100).toString();
    }

    function setSessionTimeOnLms() {

        const timeString = base.getLapsedTime();

        // convert time value into scorm 2004 format:
        // Sample: PT5H3M55S --> time span 5 hours, 3 Mminutes, 55 seconds --> 5:3:55
        const aTime = timeString.split(":"),
            hours = aTime[0],
            minutes = aTime[1],
            seconds = aTime[2];

        return lmsApi.setValue("cmi.session_time", `PT${hours}H${minutes}M${seconds}S`) === "true";
    }

    function initializeCompletionStatusFromLms() {

        const completionStatus = lmsApi.getValue("cmi.completion_status");
        if (completionStatus.toLowerCase() === "not attempted") {
            lmsApi.setValue("cmi.completion_status", "incomplete");
            return "incomplete";
        }

        return completionStatus;
    }

    function lmsSupportsMinScore() {
        const scoreChildren = lmsApi.getValue("cmi.score._children");
        return scoreChildren && (scoreChildren.indexOf("min") > -1);
    }

    function lmsSupportsMaxScore() {
        const scoreChildren = lmsApi.getValue("cmi.score._children");
        return scoreChildren && (scoreChildren.indexOf("max") > -1);
    }

    function isNotAttempted() {
        return courseScoData.get("cmi.completion_status") === "not attempted";
    }


    function initializeLmsApi() {
        const lmsResult = lmsApi.initialize();
        return (lmsResult === "true") ? lmsResult : null;
    }

    /***********************************************************************
     * initializes the course by getting initial values from lms.
     * @return {String} true / false
     ***********************************************************************/
    function initialize() {

        const lmsResult = initializeLmsApi();
        if (!lmsResult) {
            return "false";
        }

        // read only lms values -------------------------------------------------------
        courseScoData.set("cmi.completion_threshold", getCompletionThreshold() );
        courseScoData.set("cmi.credit", lmsApi.getValue("cmi.credit"));

        // read and write --------------------------------------------------
        courseScoData.set("cmi.location", lmsApi.getValue("cmi.location"));

        const completionStatus = initializeCompletionStatusFromLms();
        courseScoData.set("cmi.completion_status", completionStatus);
        courseScoData.set("cmi.success_status", lmsApi.getValue("cmi.success_status"));

        const sScoreChildren = lmsApi.getValue("cmi.score._children");
        if (sScoreChildren.indexOf("raw") > -1) {
            let ttScore = lmsApi.getValue("cmi.score.raw");
            ttScore = (isNaN(ttScore) ? 0 : ttScore * 100);
            courseScoData.set("cmi.score.raw", ttScore.toString());
        }

        const encryptedSuspendData = lmsApi.getValue("cmi.suspend_data");
        courseScoData.set("cmi.suspend_data", encryptedSuspendData);
        scoRegistry.initialize(encryptedSuspendData);

        return "true";
    }

    /***********************************************************************
     * gets the course mastery score
     * must be a value between 0 and 100
     * @return {Number} mastery score
     ***********************************************************************/
    function getCourseMasteryScore() {
        // ToDo: should be "cmi.scaled_passing_score" insted of "cmi.completion_threshold"
        const masteryScore = Number(courseScoData.get("cmi.completion_threshold"));
        return isNaN(masteryScore) ? 80 : Number(masteryScore * 100);
    }

    /***********************************************************************
     * calculates the current course score and status.
     * the refreshed values will be commit to the lms.
     * @return {String} true / false
     ***********************************************************************/
    function commit() {

        refreshCourseStatus();

        base.commitSuspendData(lmsApi, courseScoData, () => {
            LoggingService.error("You are connecteted to a scorm 1.3 LMS and the suspend_data is longer than 65000 bytes!");
        });

        courseScoData.forEachLmsRelevantValue((value, dataModelId) => {

            const stringValue = value ? value.toString() : "";

            if ((dataModelId === "cmi.score.min") && !lmsSupportsMinScore()) {
                return;
            } else if ((dataModelId === "cmi.score.max") && !lmsSupportsMaxScore()) {
                return;
            } else if (dataModelId === "cmi.success_status" && !scoRegistry.anyResultRelevantScoHasBeenInitialized()) {
                return;
            } else if (dataModelId === "cmi.progress_measure" && isNotAttempted()) {
                return;
            } else if (dataModelId === "cmi.score.raw") {
                // write score.scaled additionally
                lmsApi.setValue("cmi.score.scaled", stringValue);
            }

            lmsApi.setValue(dataModelId, stringValue);
        });

        setSessionTimeOnLms();

        return lmsApi.commit();
    }

    /***********************************************************************
     * sets the list for the scoring relevant scos
     * @param {String} seqenceIdList comma sparated value list of ids
     * @return {String} true
     ***********************************************************************/
    function setScoreList(seqenceIdList) {
        scoRegistry.setRelevantScoList(seqenceIdList);
        return "true";
    }

    /***********************************************************************
     * gets a value for lesson sco
     * @param {String} scoId the id of the sco. can be null to get a value from the lms
     * @param {String} dataModelId scorm data model id for the the value
     * @return {String} value or an empty string, if the dataModelId is not defined
     ***********************************************************************/
    function getValue(scoId, dataModelId) {

        if (scoId === null) {
            return lmsApi.getValue(dataModelId);
        }

        if (scoRegistry.contains(scoId)) {
            return scoRegistry.getValue(scoId, dataModelId);
        }

        return "";
    }

    /***********************************************************************
     * gets a value for the course
     * @param {String} dataModelId scorm data model id for the the value
     * @return {String} value or an empty string, if the does not exist
     ***********************************************************************/
    function getCourseValue(dataModelId) {

        Assert.checkArgument(dataModelId);

        if (!courseScoData.isValid(dataModelId)) {
            return "false";
        }

        return courseScoData.get(dataModelId);
    }

    // revealing public api
    return {

        initialize: initialize,
        getMasteryScore: getCourseMasteryScore,
        getCompletionThreshold: getCompletionThreshold,
        commit: commit,
        setScoreList: setScoreList,
        getValue: getValue,
        getCourseValue: getCourseValue,

        terminate: function () {
            return lmsApi.terminate();
        },

        initializeSco: function (scoId) {
            if (!scoRegistry.contains(scoId)) {
                scoRegistry.initializeNewSco(scoId);
                scoRegistry.setValue(scoId, "lesson_id", scoId);
            }

            return scoRegistry.get(scoId);
        },

        getCreApi: function () {
            return {
                getValue: function (dataModelId) {
                    if (courseScoData.isCourseManagedValue(dataModelId)) {
                        return courseScoData.get(dataModelId);
                    }
                    return lmsApi.getValue(dataModelId);
                },
                setValue: lmsApi.setValue,
                commit: commit,
                getLastError: lmsApi.getLastError,
                getErrorString: lmsApi.getErrorString,
                getDiagnostic: lmsApi.getDiagnostic
            };
        }
    };
}
